import React, { useEffect, useState, useContext } from 'react';
import { Box, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import AddDealerServiceModel from './AddDealerServiceModel';
import UpdateDealerServiceModal from './UpdateDealerServiceModal';
import { UserContext } from '../contexts/UserContext';
import { getDealerServices } from '../services/DealerServiceAPI';
import { Spinner } from './Spinner';
import { useNavigate } from 'react-router-dom';

const DescriptionCell = ({ description }) => {
  const descriptionLines = (description || '').split('\n');

  return (
    <TableCell align="left" sx={{ color: '#111111' }}>
      <Box
        sx={{
          maxWidth: { xs: 240, sm: 360, md: 520 },
          whiteSpace: { xs: 'normal', md: 'nowrap' },
          overflowX: { xs: 'visible', md: 'auto' },
          wordBreak: 'break-word'
        }}
      >
        {descriptionLines.map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </Box>
    </TableCell>
  );
};

export default function DealerServices() {
  const navigate = useNavigate();
  const { user, token } = useContext(UserContext);
  const [loaded, setLoaded] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [updatedRecord, setUpdatedRecord] = useState(null);

  useEffect(() => {
    const setUpdateService = (updatedRecord) => {
      setServices((prevServiceData) => {
        return prevServiceData.map((service) => {
          if (service.DealerID === updatedRecord?.DealerID && service.ServiceName === updatedRecord?.ServiceName) {
            return updatedRecord;
          }
          return service;
        });
      });
    };
    setUpdateService(updatedRecord);
  }, [updatedRecord]);

  useEffect(() => {
    const fetchData = async () => {
      setLoaded(true);
      try {
        const res = await getDealerServices(user.UserID, user.DealerID, token);
        setServices(res.data);
      } catch (error) {
        console.log(error);
        if (error.response.status === 400) {
          navigate('/signin');
        }
      } finally {
        setLoaded(false);
      }
    };
    fetchData();
  }, []);

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleOpenUpdateModal = () => {
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdateModal = () => {
    setIsUpdateModalOpen(false);
  };

  return (
    <>
      {loaded ? (<div className="middle" > <Spinner></Spinner></div>) : (
        <>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={11} lg={10} sx={{ mt: { xs: 3, sm: 6 }, px: { xs: 2, sm: 0 } }}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid #c52b2b',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#ffffff'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 },
                    gap: 2,
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', sm: 'row' },
                    backgroundColor: '#c52b2b',
                    color: '#ffffff'
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Dealer Services
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Manage services and pricing
                    </Typography>
                  </Box>
                  <Button
                    className="btn"
                    variant="contained"
                    onClick={handleOpenAddModal}
                    sx={{
                      backgroundColor: '#ffffff',
                      color: '#c52b2b',
                      border: '1px solid #ffffff',
                      fontWeight: 700,
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': { backgroundColor: '#f7dede', borderColor: '#f7dede' }
                    }}
                  >
                    Add Service
                  </Button>
                </Box>
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <AddDealerServiceModel open={isAddModalOpen} handleClose={handleCloseAddModal} addService={setServices} existServices={services} />
                  <UpdateDealerServiceModal open={isUpdateModalOpen} handleClose={handleCloseUpdateModal} serviceRecord={selectedService} setUpdated={setUpdatedRecord} />
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: '1px solid #f0f0f0',
                      borderRadius: 1.5,
                      overflowX: 'auto'
                    }}
                  >
                    <Table size="small" aria-label="dealer services" sx={{ minWidth: 720 }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#c52b2b' }}>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Service Name</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Description</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Cost</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Status</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      {services.length === 0 ? (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ color: '#111111', py: 4 }}>
                              No services found
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      ) : (
                        <TableBody>
                          {services
                            .slice()
                            .sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt))
                            .map((service) => (
                              <TableRow
                                key={service.ServiceName}
                                sx={{
                                  '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                  '&:last-child td, &:last-child th': { borderBottom: 0 }
                                }}
                              >
                                <TableCell component="th" scope="row" sx={{ color: '#111111' }}>{service.ServiceName}</TableCell>
                                <DescriptionCell description={service.Description} />
                                <TableCell align="left" sx={{ color: '#111111' }}>${service.Cost}</TableCell>
                                <TableCell align="left" sx={{ color: '#111111' }}>{service.Status}</TableCell>
                                <TableCell align="left">
                                  <Button
                                    className="btn"
                                    variant="contained"
                                    onClick={() => { handleOpenUpdateModal(); setSelectedService(service); }}
                                    sx={{
                                      backgroundColor: '#c52b2b',
                                      color: '#ffffff',
                                      fontWeight: 700,
                                      '&:hover': { backgroundColor: '#a82222' }
                                    }}
                                  >
                                    Update
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>)}
    </>
  );
}
